// src/services/apiService.js

const BASE_URL = '/api/v1/portfolio'; // Adjust if your Drupal base path is different

async function request(url, options = {}) {
    try {
        const response = await fetch(url, options);

        if (!response.ok) {
            let errorData = null;
            try {
                // Try to parse JSON error response from the backend
                // Use response.clone() in case we need the body again
                errorData = await response.clone().json();
            } catch (e) {
                // Ignore if response is not JSON or body is empty
            }
            // Try getting text if JSON parsing failed or wasn't attempted
            const errorText = errorData ? null : await response.text();
            const errorMessage = errorData?.message || errorText || `HTTP error! status: ${response.status}`;
            console.error("API Error Response:", errorData || response.statusText, errorText || '');
            throw new Error(errorMessage);
        }

        // Handle successful responses (response.ok is true)
        // Read the body as text first, as content-type might be unreliable
        const responseText = await response.text();

        // Check if the response body is empty or just whitespace
        if (!responseText || responseText.trim().length === 0) {
            // Return a success indicator for empty successful responses
            // (e.g., 200 OK or 204 No Content on DELETE/Clone/Unsubscribe)
            return { status: 'ok', statusCode: response.status };
        }

        // If the response body is not empty, try to parse it as JSON
        try {
            const jsonData = JSON.parse(responseText);
            return jsonData; // Return the parsed JSON data
        } catch (e) {
            // If parsing fails, it means the non-empty response wasn't valid JSON.
            // This might be unexpected, but we'll treat it as a success
            // based on the HTTP status code and return a generic success object.
            // Log a warning as this might indicate an API inconsistency.
            console.warn(`API Success Response (status ${response.status}) at ${url} was not valid JSON:`, responseText);
            return { status: 'ok', statusCode: response.status, responseBody: responseText }; // Include body for debugging
        }

    } catch (error) {
        // Catch fetch errors (network issues) or errors thrown above
        console.error("API Request Failed:", error);
        // Re-throw the error so calling components can handle it
        // Ensure it's an Error object
        if (error instanceof Error) {
            throw error;
        } else {
            throw new Error(String(error));
        }
    }
}

function encodeFormData(data) {
    return new URLSearchParams(data).toString();
}

export default {
    // --- Portfolio Endpoints ---

    listPortfolios() {
        return request(`${BASE_URL}/list`);
    },

    getPortfolio(portfolioId) {
        return request(`${BASE_URL}/${portfolioId}`);
    },

    getPortfolioDetails(portfolioId) {
        return this.getPortfolio(portfolioId);
    },

    checkUserAdmin() {
        // Assuming a generic endpoint for admin check, not tied to a specific portfolioId
        // Adjust '/api/v1/users/is-admin' to your actual endpoint
        return request(`/api/v1/users/is-admin`);
    },

    createPortfolio(data) {
        // Expected data: { name, initial_capital, currency, created_date, risk, risk_strategy }
        // Ensure created_date is in d-m-Y format before calling this
        return request(`${BASE_URL}/create`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: encodeFormData(data),
        });
    },

    updatePortfolio(portfolioId, data) {
        // Expected data: { name, risk, risk_strategy }
        return request(`${BASE_URL}/update/${portfolioId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: encodeFormData(data),
        });
    },

    resetPortfolio(portfolioId, data) {
        // Expected data: { name, initial_capital, currency, created_date, risk, risk_strategy }
        // Ensure created_date is in d-m-Y format before calling this
        return request(`${BASE_URL}/reset/${portfolioId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: encodeFormData(data),
        });
    },

    deletePortfolio(portfolioId) {
        // Uses GET as per documentation
        return request(`${BASE_URL}/delete/${portfolioId}`, {
            method: 'GET',
        });
    },

    subscribeToAlerts(portfolioId) {
        return request(`${BASE_URL}/${portfolioId}/subscribe-to-alerts`, {
            method: 'POST',
            // No body needed
        });
    },

    unsubscribeFromAlerts(portfolioId) {
        return request(`${BASE_URL}/${portfolioId}/unsubscribe-to-alerts`, {
            method: 'POST',
            // No body needed
        });
    },

    clonePortfolio(portfolioId) {
        return request(`${BASE_URL}/${portfolioId}/clone`, {
            method: 'POST',
            // No body needed
        });
    },

    // --- Daily Balance (Capital Variation) ---
    // Note: This endpoint belongs logically to Balances but is an action on a portfolio.
    addCapitalVariation(portfolioId, data) {
        // Expected data: { transaction_type (1 or 2), date (d-m-Y), description, variation_capital }
        return request(`${BASE_URL}/dailybalance/add/${portfolioId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: encodeFormData(data),
        });
    },

    // --- Waiting Positions Endpoints ---
    listWaitingPositions(portfolioId) {
        return request(`${BASE_URL}/waitings/${portfolioId}`);
    },

    addWaitingPosition(portfolioId, data) {
        // Expected data: { ticker, entry_type, direction, instrument_type, [optional fields] }
        return request(`${BASE_URL}/waitings/add/${portfolioId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: encodeFormData(data),
        });
    },

    updateWaitingPosition(portfolioId, waitingId, data) {
        // Can contain multiple fields or { field, value }
        return request(`${BASE_URL}/waitings/change/${portfolioId}/${waitingId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: encodeFormData(data),
        });
    },

    deleteWaitingPosition(portfolioId, waitingId) {
        // Uses GET as per documentation
        return request(`${BASE_URL}/waitings/delete/${portfolioId}/${waitingId}`, {
            method: 'GET',
        });
    },

    executeWaitingPosition(portfolioId, data) {
        // Expected data: { waiting_id, in_price, in_currency, currency_pair, in_date (d-m-Y), open_fee }
        return request(`${BASE_URL}/waitings/execute/${portfolioId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: encodeFormData(data),
        });
    },

    // --- Alerts ---
     getPortfolioAlerts(portfolioId) {
         // Added based on screenshot and potential need
        return request(`${BASE_URL}/alerts/${portfolioId}`);
     },

     createAlertState(portfolioId, data) {
         // Added based on screenshot and potential need
        // data: { alert_id, state ('ignore'/'error_reported'), ticker, [message] }
         return request(`${BASE_URL}/alerts/create-state/${portfolioId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: encodeFormData(data),
         });
     },

     applyAlertSolution(portfolioId, alertId) {
         // Added based on screenshot and potential need
         return request(`${BASE_URL}/alerts/apply-solution/${portfolioId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
             // API expects alert_id in body
            body: encodeFormData({ alert_id: alertId }),
         });
     },


    // --- Open Positions Endpoints ---
    listOpenPositions(portfolioId) {
        return request(`${BASE_URL}/open_positions/${portfolioId}`);
    },

    updateOpenPosition(portfolioId, openPositionId, data) {
        // Can contain multiple fields or { field, value }
        return request(`${BASE_URL}/open-positions/change/${portfolioId}/${openPositionId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: encodeFormData(data),
        });
    },

    deleteOpenPosition(portfolioId, openPositionId) {
        // Uses GET as per documentation
        return request(`${BASE_URL}/open-positions/delete/${portfolioId}/${openPositionId}`, {
            method: 'GET',
        });
    },

    closeOpenPosition(portfolioId, data) {
        // Expected data: { instrument_id, out_price, out_currency, currency_pair, out_date (d-m-Y), close_fee }
        return request(`${BASE_URL}/open_positions/close/${portfolioId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: encodeFormData(data),
        });
    },

    importOpenPosition(portfolioId, data) {
        // Expected data: { ticker, entry_type, direction, instrument_type, currency, in_currency, instrument_number, in_price, in_date, support_price, support_date, is_ipo, cfd_margin, open_fee }
        return request(`${BASE_URL}/import-open-position/${portfolioId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: encodeFormData(data),
        });
    },

    // --- Ticker Data / Autocomplete (Often needed for Import and General Analysis) ---
    getTickerAutocomplete(searchString) {
        const encodedSearch = encodeURIComponent(searchString);
        return request(`${BASE_URL}/waitings/autocomplete/${encodedSearch}`);
    },

    getAutocompleteSuggestions(searchString) {
        return this.getTickerAutocomplete(searchString);
    },

    findStockForAnalysis(symbol) {
        const encodedSymbol = encodeURIComponent(symbol);
        return request(`/api/v1/stock-finder/stocks/symbol/${encodedSymbol}`);
    },

    getTickerData(ticker, direction) {
         const encodedTicker = encodeURIComponent(ticker);
         const dir = parseInt(direction, 10);
         if (isNaN(dir) || (dir !== 0 && dir !== 1)) {
             return Promise.reject(new Error("Invalid direction provided for getTickerData"));
         }
        return request(`${BASE_URL}/ticker_data/${encodedTicker}/${dir}`);
    },

    // --- General / Statistics Endpoints ---
    getCapitalCurve(portfolioId) {
        return request(`${BASE_URL}/get-capital-curve/${portfolioId}`);
    },
    getPortfolioIndex(portfolioId, analysisId = null, symbol = null) {
        let url = `${BASE_URL}/get-portfolio-index/${portfolioId}`;
        const params = new URLSearchParams();
        if (analysisId) {
            params.append('analysis_id', analysisId);
        }
        if (symbol) {
            params.append('symbol', symbol);
        }
        const queryString = params.toString();
        if (queryString) {
            url += `?${queryString}`;
        }
        return request(url);
    },
    getRiskData(portfolioId) {
        return request(`${BASE_URL}/get-risk/${portfolioId}`);
    },
    getCyclesData(portfolioId) {
        return request(`${BASE_URL}/get-cicles/${portfolioId}`);
    },
    getCurrencyDiverData(portfolioId) {
        return request(`${BASE_URL}/get-currency-diver/${portfolioId}`);
    },
    getProfitDiverData(portfolioId) {
        return request(`${BASE_URL}/get-profit-diver/${portfolioId}`);
    },
    getInvestmentData(portfolioId) {
        return request(`${BASE_URL}/get-investment/${portfolioId}`);
    },
    getSectorsDiversificationData(portfolioId) {
        return request(`${BASE_URL}/get-sectors-diversification/${portfolioId}`);
    },
    getCountriesDiversificationData(portfolioId) {
        return request(`${BASE_URL}/get-countries-diversification/${portfolioId}`);
    },
    getCoveragesData(portfolioId) {
        return request(`${BASE_URL}/coverages/${portfolioId}`);
    },
    updateCoverage(portfolioId, currency, coverageDone) {
        const data = { currency: currency, coverage: coverageDone };
        return request(`${BASE_URL}/coverage/update-currency/${portfolioId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: encodeFormData(data),
        });
    },
    getTickersForMarketAnalysis(portfolioId) {
        return request(`${BASE_URL}/get-tickers-market-portfolio-factor/${portfolioId}`);
    },

    // --- User Roles ---
    isUserPlatino() {
        return request('/api/v1/users/is-platinum');
    },
    isUserGold() {
        return request('/api/v1/users/is-gold');
    },
    isUserBronze() {
        return request('/api/v1/users/is-bronze');
    },

    // --- History (Closed Positions) Endpoints ---
    listClosedPositions(portfolioId) {
        return request(`${BASE_URL}/histories/${portfolioId}`);
    },

    updateClosedPositionNote(portfolioId, data) {
        // Expected data: { closed_position_id, note }
        return request(`${BASE_URL}/closeds/update_note/${portfolioId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: encodeFormData(data),
        });
    },

    deleteClosedPosition(portfolioId, closedId) {
        // Uses GET as per documentation
        return request(`${BASE_URL}/closeds/delete/${portfolioId}/${closedId}`, {
            method: 'GET',
        });
    },

    // --- Daily Balances Endpoints ---
    listDailyBalances(portfolioId) {
        return request(`${BASE_URL}/dailybalance/${portfolioId}`);
    },

    addDailyBalanceOrVariation(portfolioId, data) {
        // Handles both Balance (type 0) and Variation (type 1/2)
        // data: { transaction_type, date(d-m-Y), description, [ficticious_capital, cash, real_capital] OR [variation_capital] }
        return request(`${BASE_URL}/dailybalance/add/${portfolioId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: encodeFormData(data),
        });
    },

    deleteDailyBalance(portfolioId, date) { // Date likely Y-m-d format from DB
        // Encode date just in case it contains special chars, although unlikely for Y-m-d
        const encodedDate = encodeURIComponent(date);
        // Uses GET as per documentation
        return request(`${BASE_URL}/dailybalance/delete/${portfolioId}/${encodedDate}`, {
            method: 'GET',
        });
    },

    calculateRealCapital(portfolioId, cashAmount) {
         const params = new URLSearchParams({ cash: cashAmount });
         return request(`${BASE_URL}/dailybalance/calculate-real-capital/${portfolioId}?${params.toString()}`);
    },

    calculateFictitiousCapital(portfolioId, cashAmount) {
         const params = new URLSearchParams({ cash: cashAmount });
         return request(`${BASE_URL}/dailybalance/calculate-ficticious-capital/${portfolioId}?${params.toString()}`);
    },
};

// Helper function to format Date object to d-m-Y string
export function formatDateDMY(date) {
    if (!(date instanceof Date)) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
}

// Helper function to parse d-m-Y or Y-m-d string to Date object
export function parseDate(dateString) {
    if (!dateString) return null;
    let parts;
    if (dateString.includes('-')) {
        parts = dateString.split('-');
        if (parts[0].length === 4) { // YYYY-MM-DD
            return new Date(parts[0], parts[1] - 1, parts[2]);
        } else { // DD-MM-YYYY
            return new Date(parts[2], parts[1] - 1, parts[0]);
        }
    }
    // Handle other potential formats or return null
    return null;
}

// Helper function for currency formatting
export function formatCurrency(amount, currencyCode) {
    if (typeof amount !== 'number' || !currencyCode) {
        return amount; // Return original value if invalid
    }
    try {
        return new Intl.NumberFormat(undefined, { // Use user's locale default
            style: 'currency',
            currency: currencyCode,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    } catch (e) {
        console.warn(`Could not format currency ${currencyCode}:`, e);
        return `${amount.toFixed(2)} ${currencyCode}`; // Fallback basic format
    }
}